import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { DrawerLayoutWrapper } from 'common';
import { useUserRolePermissions } from 'hooks';
import AccordionSummary from 'feature/dispatching/components/AccordionSummary';
import { useGetCarrierParams } from 'feature/dispatching/hooks';
import { formatGallons } from 'utils/helpers/format';
import TrailerDetails from './components/TrailerDetails';
import { useViewTrailerQuery } from '../trailerStatusEndpoints';
import { downloadFile, trailerCapacity } from '../utils';
import DeleteDialog from './components/DeleteDialog';
import TrailerUpdates from './components/TrailerUpdates';
import DownloadTemplatesTooltip from './components/DownloadTemplatesTooltip';

const accordionStyles = {
  border: 1,
  borderColor: 'graphic.light',
  boxShadow: 'none',
};
const accordionDetailsStyles = {
  backgroundColor: 'surface.white',
  p: 0,
  pl: 1,
  pr: 1.5,
};

const Document = ({ documentId, trailerId, title, type }) => {
  const { carrier } = useGetCarrierParams();
  return (
    <ListItem
      secondaryAction={
        documentId && (
          <IconButton edge="end" onClick={() => downloadFile(trailerId, type, carrier)}>
            <DownloadIcon color="primary" />
          </IconButton>
        )
      }
      sx={{ py: 2 }}
    >
      <>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <DescriptionIcon />
        </ListItemIcon>
        <ListItemText primary={documentId ? title : `${title} (Missing)`} />
      </>
    </ListItem>
  );
};
Document.propTypes = {
  documentId: PropTypes.string,
  title: PropTypes.string.isRequired,
  trailerId: PropTypes.string,
  type: PropTypes.oneOf(['WT', 'M27']).isRequired,
};
Document.defaultProps = {
  documentId: null,
  trailerId: null,
};

const ViewTrailerDrawer = ({ onClose, onEdit, trailerId }) => {
  const [dialogOpened, openDialog] = useState(false);
  const { isDispatchingManage } = useUserRolePermissions();

  const handleCloseDialog = () => {
    openDialog(false);
    onClose();
  };

  const { data = {}, isLoading } = useViewTrailerQuery({ trailerId });
  const {
    vaporTightnessCode,
    compartments,
    documents,
    processingHistory,
    expirationDate,
    effectiveDate,
    lastLoadedDate,
    locations,
    mexicanTrailerNumber,
    status,
  } = data;

  const compartmentsNo = (compartments?.length && compartments.length) || 1;
  const gallonsCapacity = (capacityInGallons, compartment) => (
    <ListItemText
      primary={`Compartment ${compartment}`}
      secondary={formatGallons(capacityInGallons)}
      slotProps={{
        primary: { variant: 'caption' },
        secondary: { color: 'text.primary', variant: 'subtitle2' },
      }}
    />
  );

  const handleEdit = () => {
    onEdit(trailerId);
    onClose();
  };

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
      <Button onClick={() => openDialog(true)} sx={{ width: 150 }} variant="outlined">
        Delete
      </Button>
      <Button onClick={handleEdit} sx={{ width: 150 }}>
        Edit
      </Button>
    </Box>
  );

  if (isLoading) return 'Loading...';

  return (
    <Drawer anchor="right" open>
      <DrawerLayoutWrapper
        buttons={isDispatchingManage ? buttons : null}
        headerButtonText="CLOSE"
        onHeaderButtonClick={onClose}
        title="View Trailer"
      >
        <Stack sx={{ width: 390 }}>
          <TrailerDetails
            effectiveDate={effectiveDate}
            expirationDate={expirationDate}
            lastLoadedDate={lastLoadedDate}
            locations={locations}
            mexicanTrailerNumber={mexicanTrailerNumber}
            status={status}
            vaporTightnessCode={vaporTightnessCode}
          />
          <Accordion sx={accordionStyles}>
            <AccordionSummary count={2} label="Documents">
              <DownloadTemplatesTooltip />
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsStyles}>
              <Document
                documentId={documents?.wetTest.documentId}
                title="Wet Test"
                trailerId={trailerId}
                type="WT"
              />
              <Divider />
              <Document
                documentId={documents?.method27.documentId}
                title="Method27"
                trailerId={trailerId}
                type="M27"
              />
            </AccordionDetails>
          </Accordion>

          <Accordion sx={accordionStyles}>
            <AccordionSummary count={compartmentsNo} label="Trailer Compartments" />
            <AccordionDetails sx={accordionDetailsStyles}>
              {(compartments.length &&
                compartments.map(({ capacityInGallons, position }) => (
                  <ListItem key={position}>{gallonsCapacity(capacityInGallons, position)}</ListItem>
                ))) || <ListItem key={1}> {trailerCapacity(compartments)}</ListItem>}
            </AccordionDetails>
          </Accordion>
          <TrailerUpdates updates={processingHistory} />
        </Stack>
      </DrawerLayoutWrapper>
      {dialogOpened && (
        <DeleteDialog dialogOpened onCloseDialog={handleCloseDialog} trailerId={trailerId} />
      )}
    </Drawer>
  );
};

ViewTrailerDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  trailerId: PropTypes.string.isRequired,
};

export default ViewTrailerDrawer;
